import { useContext, useEffect, useMemo, useState } from 'react';
import MenuItems from './menu-items';
import { useNavMenus } from '@/hooks/use-nav-menus';
import { Header } from './header';
import { Search } from './search';
import { Close } from './close';
import { SearchResults } from './search-results';
import { useGlobalQueries } from '@/hooks/use-global-queries';
import { useLocalization } from '@/hooks/use-localization';
import { useMobileMenus } from '@/hooks/use-mobile-menus';
import { useSearch } from '@/hooks/use-search';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { SEARCH_FAQS_QUERY_KEY, searchFaqsQuery } from '@/queries/search';
import { AppContext } from '@/contexts/app-context';
import { TabMenu, MOBILE_MENU_TABPANEL_ID, type Tab } from './tab-menu';

export const MobileMenu = () => {
    const context = useContext(AppContext);
    const { getMarketType } = useGlobalQueries();
    const { localize } = useLocalization();
    const { mainMenu } = useNavMenus();

    const getInitialActiveTab = () => {
        switch (getMarketType()) {
            case 'corporate':
                return localize('corporate');
            case 'payment_solutions':
                return localize('payment-solutions');
            case 'private':
            default:
                return localize('private');
        }
    };

    const [showMenu, setShowMenu] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [page, setPage] = useState(1);
    const [activeTab, setActiveTab] = useState(getInitialActiveTab);

    const { privateMenu, corporateMenu, paymentSolutionsMenu } = useMobileMenus({
        privateEnabled: !!getMarketType() && showMenu,
        corporateEnabled: !!getMarketType() && showMenu,
        paymentSolutionsEnabled: !!getMarketType() && showMenu,
    });
    const { searchResults } = useSearch({
        searchValue,
        page,
    });

    const searchFaqsData = useQuery({
        queryKey: [SEARCH_FAQS_QUERY_KEY, searchValue],
        queryFn: () => searchFaqsQuery(searchValue, context),
        placeholderData: keepPreviousData,
        enabled: !!searchValue,
    });

    const menuItems = useMemo(() => {
        if (getMarketType() === 'cluster') {
            return mainMenu?.data || [];
        }
        if (activeTab === localize('corporate')) {
            return corporateMenu.data ?? [];
        }
        if (activeTab === localize('payment-solutions')) {
            return paymentSolutionsMenu.data ?? [];
        }
        return privateMenu.data ?? [];
    }, [getMarketType, activeTab, localize, mainMenu?.data, corporateMenu.data, paymentSolutionsMenu.data, privateMenu.data]);

    const tabs = useMemo(() => {
        const allTabs: Tab[] = [];
        if (privateMenu.data?.length) allTabs.push({ id: 'mobile-tab-private', label: localize('private') });
        if (paymentSolutionsMenu.data?.length) allTabs.push({ id: 'mobile-tab-payment-solutions', label: localize('payment-solutions') });
        if (corporateMenu.data?.length) allTabs.push({ id: 'mobile-tab-corporate', label: localize('corporate') });
        return allTabs;
    }, [privateMenu.data, paymentSolutionsMenu.data, corporateMenu.data, localize]);

    const activeTabId = tabs.find(tab => tab.label === activeTab)?.id ?? tabs[0]?.id;

    useEffect(() => {
        if (tabs.length > 0 && !tabs.some(tab => tab.label === activeTab)) {
            setActiveTab(tabs[0].label);
        }
    }, [tabs, activeTab, setActiveTab]);

    useEffect(() => {
        // Prevent scrolling of document when menu is open
        if (showMenu) {
            document.body.style.overflow = 'hidden';
            return;
        }

        document.body.style.overflow = 'visible';

        return () => {
            if (!showMenu) {
                document.body.style.overflow = 'visible';
            }
        };
    }, [showMenu]);

    useEffect(() => {
        if (searchValue) {
            setPage(1);
        }
    }, [searchValue]);

    return (
        <>
            {!showMenu ? (
                <Header setShowMenu={setShowMenu} />
            ) : (
                <div className="scrollbar-hide r-absolute r-h-[100vh] r-w-full r-overflow-y-scroll r-bg-white md:r-hidden">
                    <div className="r-flex r-flex-col r-p-4 r-pb-24">
                        <Close setShowMenu={setShowMenu} />
                        <Search setSearchValue={setSearchValue} searchValue={searchValue} />
                        {searchValue.length < 3 ? (
                            <>
                                {getMarketType() !== 'cluster' && tabs.length > 1 && (
                                    <TabMenu tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
                                )}
                                <nav
                                    className="r-mt-10"
                                    aria-label={localize('mobile-main-menu')}
                                    {...(getMarketType() !== 'cluster' &&
                                        tabs.length > 1 && {
                                            id: MOBILE_MENU_TABPANEL_ID,
                                            role: 'tabpanel',
                                            'aria-labelledby': activeTabId,
                                        })}
                                >
                                    <MenuItems setShowMenu={setShowMenu} items={menuItems} />
                                </nav>
                            </>
                        ) : (
                            <SearchResults
                                faqHits={searchFaqsData.data?.hits || []}
                                totalFaqHits={searchFaqsData.data?.estimatedTotalHits || 0}
                                totalSearchHits={searchResults.data?.estimatedTotalHits || 0}
                                setShowMenu={setShowMenu}
                                searchValue={searchValue}
                                searchHits={searchResults.data?.hits || []}
                                page={page}
                                setPage={setPage}
                            />
                        )}
                    </div>
                </div>
            )}
        </>
    );
};
